import { useHandler } from "@redotech/react-util/hook";
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { BadgeDimensions, BadgePosition } from "./badge-collision";

export interface BadgePositionInfo {
  position: BadgePosition;
  dimensions: BadgeDimensions;
}

interface BadgePositionContextValue {
  getBadgePositions: (excludeEdgeId?: string) => BadgePositionInfo[];
  registerBadgePosition: (
    edgeId: string,
    position: BadgePosition,
    dimensions: BadgeDimensions,
  ) => void;
  unregisterBadgePosition: (edgeId: string) => void;
  badgeVersion: number;
}

const BadgePositionContext = createContext<
  BadgePositionContextValue | undefined
>(undefined);

export const BadgePositionProvider = memo(function BadgePositionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [badgePositions, setBadgePositions] = useState<
    Map<string, BadgePositionInfo>
  >(new Map());
  const [badgeVersion, setBadgeVersion] = useState(0);
  const badgePositionsRef = useRef(badgePositions);

  badgePositionsRef.current = badgePositions;

  const getBadgePositions = useCallback(
    (excludeEdgeId?: string): BadgePositionInfo[] => {
      const positions: BadgePositionInfo[] = [];
      for (const [edgeId, info] of badgePositionsRef.current) {
        if (edgeId !== excludeEdgeId) {
          positions.push(info);
        }
      }
      return positions;
    },
    [],
  );

  const registerBadgePosition = useHandler(
    (edgeId: string, position: BadgePosition, dimensions: BadgeDimensions) => {
      let shouldIncrementVersion = false;
      setBadgePositions((prev) => {
        const existing = prev.get(edgeId);
        if (
          existing &&
          existing.position.x === position.x &&
          existing.position.y === position.y &&
          existing.dimensions.width === dimensions.width &&
          existing.dimensions.height === dimensions.height
        ) {
          return prev;
        }
        shouldIncrementVersion = true;
        const next = new Map(prev);
        next.set(edgeId, { position, dimensions });
        return next;
      });

      if (shouldIncrementVersion) {
        setBadgeVersion((v) => v + 1);
      }
    },
  );

  const unregisterBadgePosition = useHandler((edgeId: string) => {
    setBadgePositions((prev) => {
      const next = new Map(prev);
      next.delete(edgeId);
      return next;
    });
  });

  const value = {
    getBadgePositions,
    registerBadgePosition,
    unregisterBadgePosition,
    badgeVersion,
  };

  return (
    <BadgePositionContext.Provider value={value}>
      {children}
    </BadgePositionContext.Provider>
  );
});

export function useBadgePositions(): BadgePositionContextValue {
  const context = useContext(BadgePositionContext);
  if (!context) {
    throw new Error(
      "useBadgePositions must be used within BadgePositionProvider",
    );
  }
  return context;
}
